import {makeAutoObservable, observable} from 'mobx'
import React from 'react'
import {Lang} from 'yougile-lang'
import {logger, ObjUtil, Util} from 'yougile-util'
import {
  createEmptyReport,
  DataObjectId,
  Hub,
  Report,
  TimetrackerBoardSettings,
  TimetrackerValue
} from 'yougile-model'
import {Items} from '@api/model/items'
import {EventBus} from '@client/event-bus'
import {minimizeChatEvent, restoreChatEvent} from '@client/events'
import {PermissionsChecker} from '@client/permissions-checker'
import {getSafeObjFromLocalStorage} from '@client/storage'
import {Sync} from '@client/sync'
import {Backend} from '@common/backend'
import {Debouncer} from '@common/debouncer'
import {DropArea} from '@common/drag/drag-common'
import {Tracking, TrackingEvent} from '@common/tracking'
import {ColumnStore} from '@components/columns/column-store'
import {showDeleteConfirm} from '@components/common/base/notifications'
import {TextAreaApi} from '@components/common/base/textarea/types'
import {DnDDataObject, DnDDataPayload} from '@components/draggable'
import {multiActionsWithTasksStore} from '@components/multi-actions-with-tasks/multi-actions-with-tasks-store'
import {DragScrollable} from '@containers/drag-scrollable'
import {createUseStore} from '@containers/hooks'
import {ExecutableQueue} from '@containers/queued-render'
import {Board} from '@extensions/board'
import {Column} from '@extensions/column'
import {canCreateMirroredTaskWithError} from '@extensions/mirrored-tasks/can-create-mirrored-task'
import {
  createMirroredTaskById,
  CreateMirroredTaskProps
} from '@extensions/mirrored-tasks/create-mirrored-task'
import {showErrorNotSupportDragFromReports} from '@extensions/mirrored-tasks/show-errors'
import {PageService} from '@extensions/page-service'
import {BoardFilters} from '@extensions/sticker/board-filters'
import {Sticker, stickerCtl} from '@extensions/sticker/sticker'
import {Task} from '@extensions/task'
import {getDropArea, multiActionMoveTasks} from './board-utils'

interface State {
  loading: boolean

  columnShowAdd: boolean
  columnShowAddExistent: boolean

  initialReport?: Report
  canEditReport?: boolean

  currentGroups: Hub[]

  timeTrackingConf?: TimetrackerBoardSettings

  draggingColumn?: DnDDataPayload<'column'>
  draggingTask?: DnDDataPayload<'task'>
  dropArea: DropArea
  columnPlaceholderHeight: number

  tasksRenderQueue: ExecutableQueue
  columnsRenderQueue: ExecutableQueue

  isAutoTaskCover: boolean
  autoTaskCoverColumnIds: Set<DataObjectId>
  showPopupCreateMirroredTask?: CreateMirroredTaskProps

  requireTimeTrackingBeforeClose: Map<
    DataObjectId,
    {
      stickerId: DataObjectId
      fromColumnId: DataObjectId
      prevPlaceholder: number
    }
  >
}

const windowResizerDebouncer = new Debouncer(50)

export class BoardStore {
  state: State

  public board: Hub

  constructor() {
    this.state = {
      loading: true,
      columnShowAdd: false,
      columnShowAddExistent: false,

      currentGroups: [],
      dropArea: getDropArea(),

      columnPlaceholderHeight: 0,

      tasksRenderQueue: new ExecutableQueue(),
      columnsRenderQueue: new ExecutableQueue(),

      isAutoTaskCover: false,
      autoTaskCoverColumnIds: new Set(),

      requireTimeTrackingBeforeClose: new Map()
    }

    this.updateTimeTrackingConf()

    makeAutoObservable<this, 'board'>(
      this,
      {
        state: observable.shallow,
        board: false
      },
      {autoBind: true}
    )
  }

  syncOnNew(board: Hub): void {
    BoardFilters.autoFilterSprints(PageService.getCurrentBoard())

    this.board = board
    this.syncOnUpdate()

    this.state.draggingColumn &&
      this.onDragColumn({type: 'column', payload: this.state.draggingColumn})
  }

  syncOnUpdate(onDone?: () => void): void {
    this.state.currentGroups = this.getGroupsSorted()
    this.updateAutoTaskCoverColumns()
    setTimeout(() => this.setLoading(false))
    this.updateTimeTrackingConf()

    if (onDone) {
      onDone()
    }
  }

  private updateTimeTrackingConf(): void {
    const box = Sync.getCurrentSandbox()
    this.state.timeTrackingConf = this.board?.stickers[
      stickerCtl.Timetracking.getSticker(box).id
    ] as TimetrackerBoardSettings | undefined
  }

  public removeStickerFromRequireTimeTracking(
    task: Hub,
    stickerValue: TimetrackerValue | undefined
  ): void {
    if (!this.state.requireTimeTrackingBeforeClose.has(task.id)) {
      return
    }

    if (!stickerValue?.plan || !stickerValue?.work) {
      const {fromColumnId, prevPlaceholder} =
        this.state.requireTimeTrackingBeforeClose.get(task.id) || {}
      const toColumn = Sync.getCurrentSandbox().getHub(fromColumnId)

      if (prevPlaceholder !== undefined && toColumn) {
        Column.taskDropHandler(prevPlaceholder, task, toColumn)
      }
    }

    this.state.requireTimeTrackingBeforeClose.delete(task.id)
  }

  private getGroupsSorted(withUiManipulations = true): Hub[] {
    if (!this.board) {
      return []
    }
    let columns = Board.listColumns(this.board)
    if (withUiManipulations) {
      columns = Items.list(
        Items.fromHub(this.board),
        columns.map(Items.fromHub)
      ).map(Items.toHub)
      columns = Util.removeDuplicateIds(columns)
    }

    const order = Board.getColumnsOrder(this.board).order
    return columns.sort((a, b) => order(a) - order(b))
  }

  private setLoading(value: boolean): void {
    this.state.loading = value
  }

  scheduleColumnHeightUpdate(): () => void {
    const resize = this.updateColumnHeightNotOften
    const onClose = this.onChatPanelOpenClose
    window.addEventListener('resize', resize)
    EventBus.on(minimizeChatEvent, onClose)
    EventBus.on(restoreChatEvent, onClose)

    return () => {
      window.removeEventListener('resize', resize)
      EventBus.off(minimizeChatEvent, onClose)
      EventBus.off(restoreChatEvent, onClose)
      this.setLoading(true)
    }
  }

  private onChatPanelOpenClose(): void {
    setTimeout(this.updateColumnHeight)
    this.state.dropArea = getDropArea()
  }

  private updateColumnHeightNotOften(): void {
    windowResizerDebouncer.runButNotOften(this.updateColumnHeight)
  }

  updateColumnHeight(): void {
    const taskListEl = document.querySelector<HTMLElement>('.task-list')
    if (!taskListEl) {
      return
    }
    const topRect = taskListEl.getBoundingClientRect()
    const height = window.innerHeight - topRect.top - 16
    taskListEl.style.height = height + 'px'
  }

  setBoardShowAdd(value: boolean): void {
    this.state.columnShowAdd = value
  }

  setBoardShowAddExistent(value: boolean): void {
    this.state.columnShowAddExistent = value
  }

  toggleBoardShowAddExistent(): void {
    this.state.columnShowAddExistent = !this.state.columnShowAddExistent
  }

  blurNewColumn(edit: TextAreaApi): void {
    if (edit.text.trim()) {
      this.submitNewColumn(edit)
    } else {
      this.cancelNewColumn()
    }
  }

  submitNewColumn(edit: TextAreaApi): void {
    if (edit.text.trim()) {
      Board.addColumn(edit.text, PageService.getCurrentBoard(), true)
      void Tracking.send(TrackingEvent.columnCreate)
      this.state.columnShowAdd = false
      this.syncOnUpdate()
    }
  }

  cancelNewColumn(): void {
    this.state.columnShowAdd = false
  }

  startEditReport(column: Hub): void {
    const report = column.data.report
    if (!report) {
      return
    }
    if (report.id !== column.id) {
      logger.log('Incorrect report id, fixing', report.id, column.id)
      report.id = column.id
      column.commit()
    }
    this.state.initialReport = column.data.report
    this.state.canEditReport = PermissionsChecker.checkRename(column)
  }

  addReport(): void {
    this.state.initialReport = createEmptyReport([])
    this.state.canEditReport = true
  }

  async deleteReport(): Promise<boolean> {
    if (!(await showDeleteConfirm(Lang.msg.boardDeleteReportConfirm))) {
      return false
    }
    if (!this.state.initialReport) {
      return false
    }
    const box = Sync.getCurrentSandbox()
    const repoColumn = box.get(this.state.initialReport.id) as Hub
    repoColumn.deleted = true
    repoColumn.commit()
    return true
  }

  saveReport(report: Report): void {
    if (!this.state.initialReport) {
      return
    }
    const box = Sync.getCurrentSandbox()
    const existent = box.get(this.state.initialReport.id) as Hub
    if (existent) {
      existent.data.report = report
      existent.title = report.title
      existent.commit()
    } else {
      const repoCol = Board.addColumnsMirror([], this.board)
      report.id = repoCol.id
      repoCol.data.report = report
      repoCol.title = report.title
      repoCol.commit()
      void Tracking.send(TrackingEvent.reportColumnCreateReportColumn)
    }
  }

  onReportEditClose(): void {
    this.state.initialReport = undefined
  }

  onMoveColumnChoose(chosenColumns: Hub[]): void {
    if (!chosenColumns?.length) {
      this.state.columnShowAddExistent = false
      return
    }
    Board.addColumnsMirror(chosenColumns, this.board)
    void Tracking.send(TrackingEvent.columnCreateMirror)
    this.state.columnShowAddExistent = false
    this.syncOnUpdate()
  }

  onDragColumnStart(drag: DnDDataPayload<'column'>): void {
    this.state.draggingColumn = drag
  }

  onDragColumnEnd(drag: DnDDataPayload<'column'>): void {
    this.state.draggingColumn = undefined
    const box = Sync.getCurrentSandbox()
    const columnToMove = box.get(drag.columnId) as Hub
    if (!columnToMove) {
      return
    }
    const ixOver = this.state.currentGroups.findIndex(
      c => c.id === columnToMove.id
    )
    if (ixOver < 0) {
      return
    }

    const boardWas = box.get(drag.boardId) as Hub
    if (boardWas.id === this.board.id) {
      const groupsOrder = Board.getColumnsOrder(this.board)
      groupsOrder.handleDrop(ixOver, columnToMove)
    } else {
      Board.columnDropHandler(ixOver, columnToMove, boardWas, this.board)
    }
  }

  onDragColumn(drag: DnDDataObject, columnId?: DataObjectId): void {
    if (drag.type !== 'column' || drag.payload.columnId === columnId) {
      return
    }

    const list = this.state.currentGroups
    const ixDrag = list.findIndex(c => c.id === drag.payload.columnId)
    const ixOver = columnId ? list.findIndex(c => c.id === columnId) : 0
    if (ixOver < 0) {
      return
    }

    ixDrag > -1 && list.splice(ixDrag, 1)
    const placeholder = Sync.getCurrentSandbox().get(
      drag.payload.columnId
    ) as Hub
    list.splice(ixOver, 0, placeholder)
    this.state.currentGroups = [...this.state.currentGroups]
  }

  onTaskDrop({
    columnStore,
    drag,
    toColumn,
    taskId,
    after
  }: {
    columnStore: ColumnStore
    drag: DnDDataObject
    after?: boolean
    toColumn: Hub
    taskId?: DataObjectId
  }): void {
    if (drag.type !== 'task') {
      return
    }

    const toColumnIsReport = !!toColumn.data?.report
    if (toColumnIsReport) {
      return
    }

    const box = Sync.getCurrentSandbox()
    const draggableTaskId = drag.payload.taskId

    if (drag.payload.shouldCreateMirror) {
      if (!Backend.features.dragFromReports) {
        showErrorNotSupportDragFromReports()
        return
      }
      const addAfterHubId = taskId

      const props = {
        taskId: draggableTaskId,
        location: toColumn.id,
        type: 'specified' as const,
        addAfterHubId,
        after
      }

      const settings = getSafeObjFromLocalStorage('dialog-no-show-more')
      if (settings['reports-drag']) {
        void Tracking.send(
          TrackingEvent.reportColumnCreateMirroredTaskViaReportColumn
        )
        void createMirroredTaskById(props)
      } else {
        if (
          !canCreateMirroredTaskWithError({
            taskId: props.taskId,
            location: props.location
          })
        ) {
          return
        }

        this.state.showPopupCreateMirroredTask = props
      }

      return
    }

    if (draggableTaskId === taskId) {
      return
    }

    // NB! it's mandatory to detect if board has timetracking rules
    const timeTrackingConf = this.state.timeTrackingConf || undefined
    const complete = (timeTrackingConf && timeTrackingConf?.complete) || {}
    const columnInComplete = complete[toColumn.id]
    if (toColumn.id && draggableTaskId && columnInComplete) {
      const taskHub = Task.getById(draggableTaskId)
      if (taskHub) {
        const pinnedStickers = Sticker.getAllPinned(taskHub)

        const timeTrackingSticker = pinnedStickers?.find(
          stickerTag => Sticker.getType(stickerTag) === 'timetracking'
        )
        if (timeTrackingSticker) {
          const stickerValue = taskHub.stickers[timeTrackingSticker.id] as
            | TimetrackerValue
            | undefined

          if (!stickerValue?.plan || !stickerValue?.work) {
            const prevColumn = box.getHub(taskHub.location)

            if (prevColumn?.id === toColumn.id) {
              return
            }

            if (prevColumn) {
              const openO = Column.getTasksOrder(prevColumn).order
              const openOrderFunc = (a: Hub, b: Hub): number =>
                openO(a) - openO(b)
              const tasksList = columnStore.getTasks(prevColumn)
              const sorted = [...tasksList].sort(openOrderFunc)

              const prevPlaceholder = sorted.findIndex(
                t => t.id === draggableTaskId
              )

              this.state.requireTimeTrackingBeforeClose.set(draggableTaskId, {
                stickerId: timeTrackingSticker.id,
                fromColumnId: prevColumn.id,
                prevPlaceholder
              })
            }
          }
        }
      }
    }

    const groupWas = box.get(drag.payload.columnId) as Hub
    const task = box.get(drag.payload.taskId) as Hub
    const anotherColumn = groupWas.id !== toColumn.id

    let placeholderIndex = 0
    if (taskId) {
      const taskList = columnStore.getOpenSortedTasks(toColumn)
      const taskIdx = taskList.findIndex(t => t.id === taskId)
      if (taskIdx >= 0) {
        placeholderIndex = taskIdx + (after ? 1 : 0)
      }

      const origTaskIdx = taskList.indexOf(task)
      if (origTaskIdx >= 0 && origTaskIdx < placeholderIndex) {
        placeholderIndex--
      }

      if (!anotherColumn && origTaskIdx === placeholderIndex) {
        // the same place, no need to move
        return
      }
    }

    if (multiActionsWithTasksStore.state.visible) {
      if (anotherColumn) {
        multiActionMoveTasks(placeholderIndex, toColumn, false)
        this.multiActionMoveTaskHook(groupWas, toColumn)
      } else {
        multiActionMoveTasks(placeholderIndex, toColumn, true)
      }
      setTimeout(() => multiActionsWithTasksStore.visibleOff(), 0)
    } else {
      if (anotherColumn) {
        Column.taskDropHandler(placeholderIndex, task, toColumn)
        void Sticker.moveTaskHook(task, groupWas, toColumn, toColumn.id).then(
          allowed => {
            if (!allowed) {
              Column.taskDropHandler(0, task, groupWas) // revert
            }
          }
        )
      } else {
        void Tracking.send(TrackingEvent.taskMoveOnBoard)
        Column.taskDropHandler(placeholderIndex, task, toColumn)
      }
    }
    columnStore.updateTaskList(toColumn)
  }

  private multiActionMoveTaskHook(groupWas: Hub, toGroup: Hub): void {
    for (const tasks of ObjUtil.values(
      multiActionsWithTasksStore.state.selectedColumnTasks
    ).reverse()) {
      for (const task of ObjUtil.keys(tasks).reverse()) {
        const taskHub = Task.getById(task)
        if (!taskHub) {
          continue
        }
        void Sticker.moveTaskHook(taskHub, groupWas, toGroup, toGroup.id).then(
          allowed => {
            if (!allowed) {
              Column.taskDropHandler(0, taskHub, groupWas) // reverting
            }
          }
        )
      }
    }
  }

  boardDragScroll(): React.MouseEventHandler {
    const el = document.querySelector('.proj-task-list')
    return el ? DragScrollable.mouseDown(el) : () => {}
  }

  get boardWidth(): number {
    const n = this.state.currentGroups.length
    const columnWidth = 296
    const margin = 8
    // n columns plus a place for a new column plus some space after
    return (n + 1) * (columnWidth + margin)
  }

  setColumnPlaceholderHeight(height: number): void {
    this.state.columnPlaceholderHeight = height
  }

  updateAutoTaskCoverColumns(): void {
    if (this.board) {
      if (this.board.data.settings?.autoTaskCover?.checked) {
        this.state.isAutoTaskCover = true
        let columnIds = this.board.data.settings?.autoTaskCover?.values
        if (!columnIds?.length) {
          columnIds = Board.listColumns(this.board, {noMirrors: true}).map(
            x => x.id
          )
        }
        this.state.autoTaskCoverColumnIds = new Set(columnIds)
      } else {
        this.state.isAutoTaskCover = false
        this.state.autoTaskCoverColumnIds = new Set()
      }
    }
  }

  setShowPopupCreateMirroredTask(value?: CreateMirroredTaskProps): void {
    this.state.showPopupCreateMirroredTask = value
  }
}

export const [BoardContext, useBoardStore] = createUseStore(BoardStore)
